import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { Minus, Plus, Trash2, ShoppingBag, ArrowRight } from "lucide-react";
import { formatPrice } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { useContent } from "@/context/ContentProvider";

const Cart = () => {
  const { items, setQty, remove, subtotal, clear } = useCart();
  const { t } = useContent();
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-[#faf7f2] pb-24 pt-[120px]" data-testid="cart-page">
      <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
        <h1 className="font-serif-d text-5xl font-semibold text-[#4b3a2f]">Tu carrito</h1>

        {items.length === 0 ? (
          <div className="mt-16 flex flex-col items-center gap-6 rounded-3xl border border-dashed border-[#a47b52]/40 bg-[#f2e9dd]/50 py-20 text-center">
            <span className="flex h-16 w-16 items-center justify-center rounded-full bg-[#f2e9dd] text-[#a47b52]">
              <ShoppingBag size={26} />
            </span>
            <div>
              <p className="font-serif-d text-2xl text-[#4b3a2f]">{t("msgs.cart_empty_title", "Tu carrito está vacío")}</p>
              <p className="mt-2 text-sm text-[#6b5b52]">{t("msgs.cart_empty_text", "Explora el catálogo y elige tu camino hacia el bienestar.")}</p>
            </div>
            <Link
              to="/catalogo"
              data-testid="cart-empty-cta"
              className="rounded-full bg-[#4b3a2f] px-8 py-3.5 text-sm font-semibold text-[#f2e9dd] transition-colors hover:bg-[#c9a24b] hover:text-[#4b3a2f]"
            >
              Ir al catálogo
            </Link>
          </div>
        ) : (
          <div className="mt-10 grid grid-cols-1 gap-10 lg:grid-cols-[1.5fr_1fr]">
            <div className="space-y-4">
              {items.map((i) => (
                <div
                  key={i.product_id}
                  data-testid={`cart-item-${i.product_id}`}
                  className="flex gap-5 rounded-2xl border border-[#e8ded4] bg-white p-4"
                >
                  <img src={i.image_url} alt={i.title} className="h-24 w-24 rounded-xl object-cover" />
                  <div className="flex flex-1 flex-col">
                    <div className="flex items-start justify-between gap-3">
                      <Link to={`/producto/${i.product_id}`} className="font-serif-d text-lg font-semibold leading-snug text-[#4b3a2f] hover:text-[#a47b52]">
                        {i.title}
                      </Link>
                      <button
                        onClick={() => remove(i.product_id)}
                        data-testid={`cart-remove-${i.product_id}`}
                        aria-label="Eliminar"
                        className="text-[#c85a48] transition-transform hover:scale-110"
                      >
                        <Trash2 size={17} />
                      </button>
                    </div>
                    <p className="mt-1 text-xs uppercase tracking-wider text-[#a47b52]">{formatPrice(i.price)} c/u</p>
                    <div className="mt-auto flex items-center justify-between pt-3">
                      <div className="flex items-center gap-3 rounded-full border border-[#4b3a2f]/15 px-2 py-1">
                        <button onClick={() => setQty(i.product_id, i.quantity - 1)} data-testid={`cart-minus-${i.product_id}`} aria-label="Menos" className="flex h-7 w-7 items-center justify-center rounded-full hover:bg-[#f2e9dd]">
                          <Minus size={14} />
                        </button>
                        <span data-testid={`cart-qty-${i.product_id}`} className="min-w-5 text-center text-sm font-semibold text-[#4b3a2f]">{i.quantity}</span>
                        <button onClick={() => setQty(i.product_id, i.quantity + 1)} data-testid={`cart-plus-${i.product_id}`} aria-label="Más" className="flex h-7 w-7 items-center justify-center rounded-full hover:bg-[#f2e9dd]">
                          <Plus size={14} />
                        </button>
                      </div>
                      <p className="font-serif-d text-xl font-semibold text-[#4b3a2f]">{formatPrice(i.price * i.quantity)}</p>
                    </div>
                  </div>
                </div>
              ))}
              <button onClick={clear} data-testid="cart-clear-button" className="text-xs uppercase tracking-widest text-[#6b5b52] hover:text-[#c85a48]">
                Vaciar carrito
              </button>
            </div>

            <aside className="h-fit rounded-3xl border border-[#e8ded4] bg-white p-8 lg:sticky lg:top-24">
              <h2 className="font-serif-d text-2xl font-semibold text-[#4b3a2f]">Resumen del pedido</h2>
              <div className="mt-6 space-y-3 text-sm text-[#6b5b52]">
                <div className="flex justify-between"><span>Artículos</span><span>{items.reduce((s, i) => s + i.quantity, 0)}</span></div>
                <div className="flex justify-between"><span>Subtotal</span><span>{formatPrice(subtotal)}</span></div>
                <div className="flex justify-between border-t border-[#e8ded4] pt-3 text-base font-semibold text-[#4b3a2f]">
                  <span>Total</span><span data-testid="cart-total">{formatPrice(subtotal)} USD</span>
                </div>
              </div>
              <button
                onClick={() => navigate("/checkout")}
                data-testid="cart-checkout-button"
                className="mt-8 flex w-full items-center justify-center gap-2 rounded-full bg-[#c9a24b] py-4 text-sm font-semibold text-[#4b3a2f] transition-all hover:bg-[#b8913b]"
              >
                Continuar al pago <ArrowRight size={16} />
              </button>
              <p className="mt-4 text-center text-xs text-[#6b5b52]">Pago seguro procesado con PayPal</p>
            </aside>
          </div>
        )}
      </div>
    </div>
  );
};

export default Cart;
